import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { APIReturnType, UserData } from '../../types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../hooks/useAPI';
import { useGlobalAtoms } from '../../atoms/globalAtoms';

interface AuthContextData {
  user?: UserData;
  isAuthenticated?: boolean;
  token?: string;
  signIn: (email: string, password: string) => Promise<APIReturnType>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<APIReturnType>,
  resetPassword: (email: string, password: string, password_confirmation: string, token: string) => Promise<APIReturnType>;
  signup: (formData: any) => Promise<APIReturnType>;
  updateProfile: (formData: any, id: string) => Promise<APIReturnType>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextData>({
  user: {},
  signIn: async (email: string, password: string) => ({ errors: null, data: null }),
  signOut: async () => {},
  forgotPassword: async (email: string) => ({ errors: null, data: null }),
  resetPassword: async (email: string, password: string, password_confirmation: string, token: string) => ({ errors: null, data: null }),
  signup: async (formData: any) => ({ errors: null, data: null }),
  updateProfile: async (formData: any, id: string) => ({ errors: null, data: null }),
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserData>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {fetchData, token, setToken} = useAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = useRef(false);
  const { permissions, setPermissions } = useGlobalAtoms();

  useEffect(() => {
    if (token && token !== '') return;
    if (isLoading.current) return;
    const storedToken = localStorage.getItem('token');

    if (storedToken) {
      isLoading.current = true;
      validateToken(storedToken).then(() => {
        isLoading.current = false;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdminUrl = (pathname: string) => {
    return (
      pathname == '/'
      || pathname == '/users' || pathname.startsWith('/users/')
      || pathname == '/products' || pathname.startsWith('/products/')
      || pathname == '/templates' || pathname.startsWith('/templates/')
      || pathname == '/upload-tags' || pathname.startsWith('/upload-tags/')
      || pathname == '/tags' || pathname.startsWith('/tags/')
      || pathname == '/clients' || pathname.startsWith('/clients/')
      || pathname == '/sectors' || pathname.startsWith('/sectors/')
      || pathname == '/bars' || pathname.startsWith('/bars/')
      || pathname == '/categories' || pathname.startsWith('/categories/')
    );
  }

  useEffect(() => {
    if (isLoading.current) return;

    if (isAuthenticated) {
      if (location.pathname === '/signin' || location.pathname === '/signup') {
        navigate('/');
      }
    } else {
      if (isAdminUrl(location.pathname)) {
        navigate('/signin');
      }
    }
  }, [isAuthenticated, location.pathname, navigate]);

  const validateToken = async (token: string) => {
    try {
      const {errors, data} = await fetchData(
        '/admin_user',
        {},
        'GET',
        token,
      );
      if (errors) {
        updateToken('');
        updateUser({});
      } else {
        updateToken(token);
        updateUser(data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error validating token:', error);
      updateToken('');
    }
  };

  const updateToken = async (token: string) => {
    setToken(token);
    localStorage.setItem('token', token);
  }

  const updateUser = async (data: UserData) => {
    setUser(data);
    localStorage.setItem('userData', JSON.stringify(data));
  }

  const signIn = async (email: string, password: string) => {
    const {errors, data} = await fetchData(
      '/admin/signin',
      {email, password},
      'POST',
    );

    if (errors) {
      return {errors, data: null};
    } else {
      updateToken(data.token ?? '');
      updateUser(data.user);
      setIsAuthenticated(true);
      navigate('/');
      return {errors: null, data: null};
    }
  };

  const forgotPassword = async (email: string) => {
    const {errors, data} = await fetchData('/admin/password/forgot', {email}, 'POST');
    return {errors, data};
  };

  const resetPassword = async (email: string, password: string, password_confirmation: string, token: string) => {
    const {errors, data} = await fetchData(
      '/admin/password/reset',
      {email, password, password_confirmation, token},
      'POST',
    );
    return {errors, data};
  };

  const signOut = () => {
    setPermissions([]);
    setIsAuthenticated(false);
    updateUser({});
    updateToken('');
    navigate('/signin');
  };

  const signup = async (formData: any) => {
    const {errors, data} = await fetchData('/admin/signup', formData, 'POST');
    if (errors) {
      return {errors, data: null};
    } else {
      const {token, user: _user} = data;
      setToken(token);
      setIsAuthenticated(true);
      setUser(_user);
      navigate('/verify-email');
      return {errors: null, data: null};
    }
  };

  const updateProfile = async (formData: any, id: string) => {
    const {errors, data} = await fetchData(
      '/profile',
      {...formData, id},
      'POST',
    );
    if (errors) {
      return {errors, data};
    } else {
      const {token, user: _user} = data;
      if (token) {
        setToken(token);
      }
      setUser(_user);
      navigate('/');
      return {errors: null, data: null};
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      token,
      signIn,
      signOut,
      forgotPassword,
      resetPassword,
      signup,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
